// Iter 104 — Extracted from SwipeCards.js verbatim. Bundles Intel + its
// internal helpers (RecentlyDismissed, NotifDeepDive, DecisionJournal).
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { hapticTap, hapticMedium } from "../../utils/haptics";
import { f$, fN, fP, API } from "./_shared";
import { Skel } from "./Skel";

function RecentlyDismissed({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [explaining, setExplaining] = useState({}); // {id: loading|text}
  const load = async () => {
    try {
      const r = await axios.get(`${API}/notifications/dismissed?limit=10`);
      setRows(r.data?.dismissed || []);
    } catch {
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const explain = async (id) => {
    setExplaining((m) => ({ ...m, [id]: "loading" }));
    try {
      const r = await axios.post(`${API}/notifications/deep-dive`, { id });
      const txt = r.data?.summary || "Omega couldn't find context right now.";
      setExplaining((m) => ({ ...m, [id]: txt }));
    } catch {
      setExplaining((m) => ({ ...m, [id]: "AI explainer unavailable right now." }));
    }
  };

  const undismiss = async (id) => {
    hapticTap();
    try { await axios.post(`${API}/notifications/undismiss`, { id }); } catch {}
    load();
    onChanged && onChanged();
  };

  if (!rows) {
    return (
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "0.5px solid rgba(255,255,255,0.06)" }}>
        <Skel w="60%" h={10} mb={6} />
        <Skel w="100%" h={40} mb={0} radius={8} />
      </div>
    );
  }
  if (rows.length === 0) return null;

  return (
    <div data-testid="recently-dismissed" style={{ marginTop: 14, paddingTop: 12, borderTop: "0.5px solid rgba(255,255,255,0.06)" }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#8b949e", marginBottom: 8 }}>
        RECENTLY DISMISSED · {rows.length}
      </div>
      {rows.map((n) => {
        const id = n.id;
        const ex = explaining[id];
        return (
          <div key={id} style={{ marginBottom: 8, padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#e6edf3", marginBottom: 2 }}>{n.title || n.ticker || "Dismissed"}</div>
            <div style={{ fontSize: 10, color: "#8b949e", lineHeight: 1.4, marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
              {n.message || ""}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                data-testid={`why-missed-${id}`}
                onClick={() => explain(id)}
                disabled={ex === "loading"}
                style={{
                  fontSize: 10, fontWeight: 700, padding: "5px 10px", borderRadius: 8,
                  background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.28)",
                  color: "#a78bfa", cursor: "pointer", WebkitTapHighlightColor: "transparent",
                }}
              >{ex === "loading" ? "Thinking…" : "Why did I miss this?"}</button>
              <button
                onClick={() => undismiss(id)}
                style={{
                  fontSize: 10, fontWeight: 700, padding: "5px 10px", borderRadius: 8,
                  background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.25)",
                  color: "#60a5fa", cursor: "pointer", WebkitTapHighlightColor: "transparent",
                }}
              >↶ Restore</button>
            </div>
            {ex && ex !== "loading" && (
              <div style={{ marginTop: 8, padding: 8, borderRadius: 8, background: "rgba(167,139,250,0.05)", borderLeft: "2px solid #a78bfa", fontSize: 11, color: "#c9d1d9", lineHeight: 1.5 }}>
                {ex}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ═══ NOTIF DEEP-DIVE — richer detail view with AI summary + call/put payoff ═══
function NotifDeepDive({ notif, accent, onBack }) {
  const [dive, setDive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    let stop = false;
    const id = notif.id || notif._id;
    if (!id) { setLoading(false); setError(true); return; }
    (async () => {
      try {
        const r = await axios.post(`${API}/notifications/deep-dive`, { id });
        if (!stop) setDive(r.data);
      } catch {
        if (!stop) setError(true);
      } finally {
        if (!stop) setLoading(false);
      }
    })();
    return () => { stop = true; };
  }, [notif.id, notif._id]);

  const verdictColor = dive?.verdict === "bullish" ? "#4ade80"
                     : dive?.verdict === "bearish" ? "#f87171"
                     : "#60a5fa";

  const chartData = dive && dive.callPayoff?.length > 0 && dive.putPayoff?.length > 0 ? {
    labels: dive.callPayoff.map(p => `$${p.spot}`),
    datasets: [
      { label: "Call", data: dive.callPayoff.map(p => p.pnl), borderColor: "#4ade80", backgroundColor: "rgba(74,222,128,0.08)", fill: true, borderWidth: 1.8, pointRadius: 0, tension: 0.1 },
      { label: "Put",  data: dive.putPayoff.map(p => p.pnl),  borderColor: "#f87171", backgroundColor: "rgba(248,113,113,0.08)", fill: true, borderWidth: 1.8, pointRadius: 0, tension: 0.1 },
    ],
  } : null;
  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: true, labels: { color: "#8b949e", font: { size: 9 }, boxWidth: 8, boxHeight: 8 }, position: "top", align: "end" },
      tooltip: { mode: "index", intersect: false, backgroundColor: "rgba(13,17,23,0.95)", titleFont: { size: 10 }, bodyFont: { size: 10 } },
    },
    scales: {
      x: { ticks: { color: "#636e7b", font: { size: 7 }, maxTicksLimit: 5 }, grid: { display: false } },
      y: { ticks: { color: "#636e7b", font: { size: 8 }, callback: v => `$${v}` }, grid: { color: "rgba(255,255,255,0.03)" } },
    },
  };

  return (
    <div style={{ padding: "0 16px 32px" }} data-testid="notif-deep-dive">
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", fontSize: 13, padding: 0, marginBottom: 14 }}>&larr; Back to feed</button>

      <div style={{ borderRadius: 16, padding: 16, background: "rgba(255,255,255,0.02)", border: `1px solid ${accent}30`, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: accent }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: accent, letterSpacing: 1.5 }}>{(notif.type || notif.category || "intel").toUpperCase().replace(/_/g, " ")}</span>
        </div>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#e6edf3", lineHeight: 1.3, marginBottom: 4 }}>{notif.title}</div>
        {notif.ticker && <div style={{ fontSize: 13, fontWeight: 600, color: "#60a5fa", marginBottom: 8 }}>{notif.ticker}{dive?.quote?.price ? <span style={{ color: "#8b949e", fontWeight: 400 }}> · spot ${dive.quote.price.toFixed(2)}</span> : null}</div>}
        <div style={{ fontSize: 13, color: "#c9d1d9", lineHeight: 1.65 }}>{notif.message}</div>
        <div style={{ fontSize: 10, color: "#636e7b", marginTop: 10 }}>{notif.createdAt ? new Date(notif.createdAt).toLocaleString() : ""}</div>
      </div>

      <div style={{ borderRadius: 16, padding: 14, background: "linear-gradient(135deg, rgba(167,139,250,0.1), rgba(96,165,250,0.04))", border: "1px solid rgba(167,139,250,0.22)", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", letterSpacing: 1.5 }}>OMEGA ZI AI · DEEP DIVE</span>
          {dive?.verdict && (
            <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6, background: `${verdictColor}22`, color: verdictColor, letterSpacing: 1 }}>
              {String(dive.verdict).toUpperCase()}
            </span>
          )}
        </div>
        {loading ? (
          <>
            <Skel w="100%" h={12} mb={6} />
            <Skel w="90%" h={12} mb={6} />
            <Skel w="60%" h={12} mb={0} />
          </>
        ) : error || !dive ? (
          <div style={{ fontSize: 12, color: "#8b949e" }}>AI deep-dive unavailable right now. Tap "Read the full story" below.</div>
        ) : (
          <>
            <div data-testid="deep-dive-summary" style={{ fontSize: 13, color: "#e6edf3", lineHeight: 1.6, marginBottom: 10 }}>{dive.summary || "No summary generated."}</div>
            {dive.recommendation && (
              <div style={{ padding: 10, borderRadius: 10, background: "rgba(0,0,0,0.25)", borderLeft: `3px solid ${verdictColor}`, marginTop: 6 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: verdictColor, letterSpacing: 1, marginBottom: 3 }}>RECOMMENDATION</div>
                <div data-testid="deep-dive-recommendation" style={{ fontSize: 12, color: "#c9d1d9", lineHeight: 1.5 }}>{dive.recommendation}</div>
              </div>
            )}
          </>
        )}
      </div>

      {chartData && (
        <div data-testid="deep-dive-chart" style={{ borderRadius: 16, padding: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#8b949e", letterSpacing: 1.5 }}>FORECAST · CALL VS PUT</div>
              <div style={{ fontSize: 10, color: "#636e7b", marginTop: 2 }}>ATM strike ${dive.atmStrike} · premium ~${dive.atmPremium} · 30-DTE IV proxy</div>
            </div>
          </div>
          <div style={{ height: 150, marginTop: 4 }}>
            <Line data={chartData} options={chartOpts} />
          </div>
          <div style={{ fontSize: 9, color: "#636e7b", marginTop: 6, fontStyle: "italic" }}>
            P&amp;L (per 1 contract, $100 multiplier) at expiry across ±10% spot range. Educational — not a live order.
          </div>
        </div>
      )}

      {(dive?.readMoreUrl || notif.data?.url) && (
        <a
          data-testid="deep-dive-read-more"
          href={dive?.readMoreUrl || notif.data?.url}
          target="_blank" rel="noreferrer"
          style={{
            display: "block", textAlign: "center", textDecoration: "none",
            padding: "12px 14px", borderRadius: 12,
            background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.3)",
            color: "#60a5fa", fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
          }}
        >
          Read the full story {dive?.source ? `on ${dive.source} →` : "→"}
        </a>
      )}
    </div>
  );
}


export function IntelExpanded() {
  const [notifs, setNotifs] = useState(null);
  const [selected, setSelected] = useState(null);
  const [lastDecision, setLastDecision] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [mutes, setMutes] = useState({});
  const [digestMode, setDigestMode] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);
  const pressTimerRef = useRef(null);
  const pressFiredRef = useRef(false);

  const loadAll = async () => {
    try {
      const [nr, dr] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/autopilot/decisions?limit=1`).catch(() => ({ data: { decisions: [] } })),
      ]);
      // Normalize both response shapes (list OR { notifications: [...] })
      const list = Array.isArray(nr.data) ? nr.data : (nr.data?.notifications || []);
      setNotifs(list.slice(0, 40));
      if (nr.data?.mutes) setMutes(nr.data.mutes || {});
      if (typeof nr.data?.digestMode === "boolean") setDigestMode(nr.data.digestMode);
      const latest = (dr.data?.decisions || dr.data || [])[0] || null;
      setLastDecision(latest);
    } catch {
      setNotifs([]);
    }
  };
  useEffect(() => { loadAll(); const iv = setInterval(loadAll, 20000); return () => clearInterval(iv); }, []);

  // Digest preview — poll only while Settings panel is open and digestMode is ON
  useEffect(() => {
    if (!showSettings || !digestMode) { setDigestPreview(null); return; }
    let stop = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/notifications/digest-preview`);
        if (!stop) setDigestPreview(r.data);
      } catch {}
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => { stop = true; clearInterval(iv); };
  }, [showSettings, digestMode]);

  // Toggle a category mute and persist
  const toggleMute = async (cat) => {
    const next = { ...mutes, [cat]: !mutes[cat] };
    setMutes(next);
    hapticTap();
    try { await axios.post(`${API}/notifications/mutes`, { mutes: next, digestMode }); } catch {}
  };
  const toggleDigest = async () => {
    const next = !digestMode;
    setDigestMode(next);
    hapticTap();
    try { await axios.post(`${API}/notifications/mutes`, { mutes, digestMode: next }); } catch {}
  };

  // Long-press (500ms) on a notif row → dismiss + fade
  const dismissNotif = async (id) => {
    if (!id) return;
    setDismissingId(id);
    hapticMedium();
    // Optimistic remove after fade
    setTimeout(() => {
      setNotifs((prev) => (prev || []).filter((x) => (x.id || x._id) !== id));
      setDismissingId(null);
    }, 220);
    try { await axios.post(`${API}/notifications/dismiss`, { id, snoozeHours: 24 }); } catch {}
  };
  const startPress = (id) => {
    pressFiredRef.current = false;
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      pressFiredRef.current = true;
      dismissNotif(id);
    }, 500);
  };
  const cancelPress = () => {
    if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; }
  };

  // Open a notif → mark it read immediately (optimistic) + POST to server.
  const openNotif = (n) => {
    const id = n.id || n._id;
    if (id && !n.read) {
      setNotifs((prev) => (prev || []).map((x) => ((x.id || x._id) === id ? { ...x, read: true } : x)));
      // Tell the parent grid so its Intel tile badge decrements immediately.
      try { window.dispatchEvent(new CustomEvent("zi-notif-read", { detail: { id } })); } catch {}
      axios.post(`${API}/notifications/read`, { id }).catch(() => {});
    }
    setSelected(n);
  };

  const forceGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/notifications/generate`);
      await loadAll();
      hapticTap();
    } catch {}
    setGenerating(false);
  };

  // Colors by category / type
  const cc = { trade: "#4ade80", scan: "#60a5fa", risk: "#f87171", tea: "#fbbf24", alert: "#a78bfa", order: "#4ade80", intel: "#a78bfa" };
  const typeIcon = (t) => {
    if (!t) return "●";
    if (t.includes("ipo")) return "IPO";
    if (t.includes("earnings")) return "EPS";
    if (t.includes("upgrade")) return "▲";
    if (t.includes("downgrade")) return "▼";
    if (t.includes("volume_spike")) return "◆";
    if (t.includes("halt")) return "⏸";
    if (t.includes("ai_decision")) return "AI";
    if (t.includes("tea")) return "☕";
    if (t.startsWith("order_")) return "$";
    return "●";
  };

  if (notifs === null) {
    return (
      <div style={{ padding: "0 16px 32px" }} data-testid="intel-expanded-skel">
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <Skel w={80} h={34} mb={6} radius={8} />
          <Skel w={140} h={12} mb={0} />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 0", borderBottom: "0.5px solid rgba(255,255,255,0.04)" }}>
            <div className="zi-skel" style={{ width: 10, height: 10, borderRadius: "50%", marginTop: 3 }} />
            <div style={{ flex: 1 }}>
              <Skel w="60%" h={14} mb={6} />
              <Skel w="90%" h={10} mb={3} />
              <Skel w="40%" h={9} mb={0} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (selected) return <NotifDeepDive notif={selected} accent={cc[selected.category] || "#60a5fa"} onBack={() => setSelected(null)} />;

  if (showJournal) return <DecisionJournal onBack={() => setShowJournal(false)} />;

  // Group notifs by category
  const grouped = notifs.reduce((acc, n) => {
    const c = n.category || "intel";
    (acc[c] = acc[c] || []).push(n);
    return acc;
  }, {});
  const grpOrder = ["order", "intel", "trade", "tea", "risk", "alert"];
  const grpLabel = { order: "Executions", intel: "Intelligence · News · Events", trade: "Trades", tea: "Tea Scores", risk: "Risk", alert: "Alerts" };

  return (
    <div style={{ padding: "0 16px 32px" }}>
      {/* Last-AI-decision banner */}
      {lastDecision && (
        <button
          data-testid="last-ai-decision-banner"
          onClick={() => setShowJournal(true)}
          style={{
            width: "100%", textAlign: "left", cursor: "pointer",
            marginBottom: 14, padding: 14, borderRadius: 16,
            background: "linear-gradient(135deg, rgba(167,139,250,0.12), rgba(96,165,250,0.06))",
            border: "1px solid rgba(167,139,250,0.25)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", letterSpacing: 1.5 }}>LAST OMEGA DECISION</span>
            </div>
            <span style={{ fontSize: 10, color: "#a78bfa", fontWeight: 600 }}>Journal →</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#e6edf3", marginBottom: 4 }}>
            {lastDecision.symbol || lastDecision.ticker || "?"} · {lastDecision.action || "EVALUATE"}
          </div>
          <div style={{ fontSize: 11, color: "#c9d1d9", lineHeight: 1.45 }}>
            Zi {lastDecision.final_score ?? lastDecision.ziScore ?? "?"} · regime {(lastDecision.regime || "—").replace("_", " ")}
            {lastDecision.kellyMultiplier ? ` · Kelly ${Math.round(lastDecision.kellyMultiplier * 100)}%` : ""}
          </div>
        </button>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#e6edf3", lineHeight: 1 }}>{(notifs || []).filter(n => !n.read).length}</div>
          <div style={{ fontSize: 11, color: "#636e7b" }}>Unread · {(notifs || []).length} total</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            data-testid="intel-settings-toggle"
            onClick={() => { setShowSettings((v) => !v); hapticTap(); }}
            aria-label="Notification settings"
            style={{
              width: 36, height: 36, borderRadius: 10,
              border: `1px solid ${showSettings ? "rgba(167,139,250,0.4)" : "rgba(255,255,255,0.08)"}`,
              background: showSettings ? "rgba(167,139,250,0.12)" : "rgba(255,255,255,0.03)",
              color: showSettings ? "#a78bfa" : "#8b949e",
              fontSize: 16, cursor: "pointer",
              WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
            }}
          >⚙</button>
          <button
            data-testid="intel-refresh"
            onClick={forceGenerate}
            disabled={generating}
            style={{
              padding: "8px 12px", borderRadius: 10, border: "1px solid rgba(96,165,250,0.22)",
              background: "rgba(96,165,250,0.1)", color: "#60a5fa", fontSize: 11, fontWeight: 700, cursor: "pointer",
              WebkitTapHighlightColor: "transparent", touchAction: "manipulation",
            }}
          >{generating ? "Scanning…" : "↻ Refresh feed"}</button>
        </div>
      </div>

      {/* Settings panel — per-category mutes + digest mode */}
      {showSettings && (
        <div data-testid="notif-settings-panel" style={{
          marginBottom: 16, padding: 14, borderRadius: 14,
          background: "rgba(167,139,250,0.04)",
          border: "1px solid rgba(167,139,250,0.15)",
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#a78bfa", marginBottom: 10 }}>NOTIFICATION CONTROL</div>
          {[
            { key: "intel", label: "Intelligence · News · Events", color: "#a78bfa" },
            { key: "trade", label: "Trades · Fills", color: "#4ade80" },
            { key: "tea", label: "Tea Scores · Signals", color: "#fbbf24" },
            { key: "risk", label: "Risk · Drawdown", color: "#f87171" },
            { key: "order", label: "Executions · Orders", color: "#60a5fa" },
          ].map((c) => (
            <div key={c.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "0.5px solid rgba(255,255,255,0.04)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} />
                <span style={{ fontSize: 12, color: "#e6edf3" }}>{c.label}</span>
              </div>
              <button
                data-testid={`notif-mute-${c.key}`}
                onClick={() => toggleMute(c.key)}
                style={{
                  position: "relative", width: 42, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                  background: mutes[c.key] ? "rgba(255,255,255,0.08)" : c.color,
                  transition: "background 0.2s ease",
                  WebkitTapHighlightColor: "transparent",
                }}
                aria-pressed={!mutes[c.key]}
                aria-label={`${mutes[c.key] ? "Unmute" : "Mute"} ${c.label}`}
              >
                <span style={{
                  position: "absolute", top: 3, left: mutes[c.key] ? 3 : 21, width: 18, height: 18,
                  borderRadius: "50%", background: "#fff", transition: "left 0.2s ease",
                }} />
              </button>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0 4px", marginTop: 6, borderTop: "0.5px solid rgba(255,255,255,0.06)" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "#e6edf3", fontWeight: 600 }}>Digest mode</div>
              <div style={{ fontSize: 10, color: "#8b949e", marginTop: 2, lineHeight: 1.4 }}>
                Muted categories bundle into a single<br/>rollup push every 5 min (instead of silent).
              </div>
              {digestMode && digestPreview && (
                <div data-testid="digest-live-preview" style={{
                  marginTop: 6, fontSize: 10, color: "#a78bfa", fontWeight: 600,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa", animation: "pulse 1.5s infinite" }} />
                  Next rollup in {Math.floor((digestPreview.etaSeconds || 0) / 60)}:
                  {String((digestPreview.etaSeconds || 0) % 60).padStart(2, "0")}
                  {" · "}{digestPreview.bundled || 0} bundled
                </div>
              )}
            </div>
            <button
              data-testid="notif-digest-toggle"
              onClick={toggleDigest}
              style={{
                position: "relative", width: 42, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                background: digestMode ? "#a78bfa" : "rgba(255,255,255,0.08)",
                transition: "background 0.2s ease",
                WebkitTapHighlightColor: "transparent", marginLeft: 12, flexShrink: 0,
              }}
              aria-pressed={digestMode}
            >
              <span style={{
                position: "absolute", top: 3, left: digestMode ? 21 : 3, width: 18, height: 18,
                borderRadius: "50%", background: "#fff", transition: "left 0.2s ease",
              }} />
            </button>
          </div>
          <div style={{ fontSize: 10, color: "#636e7b", marginTop: 10, fontStyle: "italic" }}>
            Tip: long-press any row to dismiss it for 24h.
          </div>

          {/* RECENTLY DISMISSED · "Why did I miss this?" */}
          <RecentlyDismissed onChanged={loadAll} />
        </div>
      )}

      {notifs.length === 0 && (
        <div style={{ padding: "32px 16px", textAlign: "center", borderRadius: 16, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#e6edf3", marginBottom: 4 }}>Feed is quiet</div>
          <div style={{ fontSize: 11, color: "#8b949e", lineHeight: 1.5 }}>
            Omega scans market news, earnings, IPO watches, whale flow &amp; AI decisions every 5 min.<br/>
            Tap <strong>↻ Refresh feed</strong> above to force a scan right now.
          </div>
        </div>
      )}

      {grpOrder.filter(g => grouped[g]?.length).map(grp => (
        <div key={grp} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 10, color: "#8b949e", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>
            {grpLabel[grp]} · {grouped[grp].length}
          </div>
          {grouped[grp].map((n, i) => {
            const rowId = n.id || n._id;
            const isDismissing = dismissingId && dismissingId === rowId;
            return (
            <div key={rowId || i} data-testid={`intel-row-${n.type || grp}-${i}`}
              onClick={() => { if (pressFiredRef.current) { pressFiredRef.current = false; return; } openNotif(n); }}
              onPointerDown={() => startPress(rowId)}
              onPointerUp={cancelPress}
              onPointerLeave={cancelPress}
              onPointerCancel={cancelPress}
              onContextMenu={(e) => e.preventDefault()}
              style={{
                display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 4px",
                borderBottom: "0.5px solid rgba(255,255,255,0.04)",
                cursor: "pointer", WebkitTapHighlightColor: "transparent",
                opacity: isDismissing ? 0 : 1,
                transform: isDismissing ? "translateX(40px)" : "translateX(0)",
                transition: "opacity 0.22s ease, transform 0.22s ease",
                userSelect: "none",
              }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: `${cc[n.category] || "#60a5fa"}22`, color: cc[n.category] || "#60a5fa", flexShrink: 0, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", letterSpacing: 0 }}>
                {typeIcon(n.type)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3", marginBottom: 2 }}>{n.title || n.ticker || "Alert"}</div>
                <div style={{ fontSize: 11, color: "#8b949e", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {n.message || ""}
                </div>
                <div style={{ fontSize: 9, color: "#636e7b", marginTop: 3 }}>{n.createdAt ? new Date(n.createdAt).toLocaleString() : ""}</div>
              </div>
              {!n.read && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#60a5fa", marginTop: 10, flexShrink: 0 }} />}
            </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ═══ DECISION JOURNAL — scrollable timeline of Omega autopilot decisions ═══
function DecisionJournal({ onBack }) {
  const [items, setItems] = useState(null);
  const [regimeFilter, setRegimeFilter] = useState("ALL");
  const [replayId, setReplayId] = useState(null); // which decision is currently animating
  const [replayProgress, setReplayProgress] = useState(0); // 0..1
  const replayRafRef = useRef(null);
  useEffect(() => {
    axios.get(`${API}/autopilot/decisions?limit=50`)
      .then(r => setItems(r.data?.decisions || r.data || []))
      .catch(() => setItems([]));
  }, []);

  // Play a replay animation — 1.4 s eased ramp from 0 → final score.
  const playReplay = (id) => {
    hapticMedium();
    if (replayRafRef.current) cancelAnimationFrame(replayRafRef.current);
    setReplayId(id);
    setReplayProgress(0);
    const start = performance.now();
    const DURATION = 1400;
    const step = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setReplayProgress(eased);
      if (t < 1) {
        replayRafRef.current = requestAnimationFrame(step);
      } else {
        replayRafRef.current = null;
      }
    };
    replayRafRef.current = requestAnimationFrame(step);
  };
  useEffect(() => () => { if (replayRafRef.current) cancelAnimationFrame(replayRafRef.current); }, []);

  if (items === null) return <div style={{ padding: 20, color: "#8b949e" }}><Skel w="100%" h={80} mb={10} radius={12} /><Skel w="100%" h={80} mb={10} radius={12} /><Skel w="100%" h={80} mb={10} radius={12} /></div>;

  // Regime filter chips — derive unique regimes from the data
  const allRegimes = Array.from(new Set(items.map(d => d.regime).filter(Boolean)));
  const filtered = regimeFilter === "ALL" ? items : items.filter(d => d.regime === regimeFilter);

  // Regime ribbon: last 30 decisions → compressed stripe of colored segments.
  // Each regime gets a stable color; adjacent same-regime cells merge visually.
  const regimeColors = {
    trending_up: "#4ade80", trending_down: "#f87171",
    high_vol: "#fbbf24", low_vol: "#60a5fa",
    consolidation: "#a78bfa", unknown: "#64748b",
  };
  const ribbon = items.slice(0, 30).map(d => ({ r: d.regime || "unknown", t: d.createdAt })).reverse();

  return (
    <div style={{ padding: "0 16px 32px" }} data-testid="decision-journal">
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", fontSize: 13, padding: 0, marginBottom: 14 }}>&larr; Back to Intel</button>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3", marginBottom: 4 }}>Decision Journal</div>
      <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 14 }}>Every Omega autopilot decision with full 12-vector breakdown. Tap ▶ on any row to replay the Zi ramp.</div>

      {/* JOURNAL INSIGHTS — rolling snapshot of the last 10 decisions */}
      {items.length > 0 && (() => {
        const last = items.slice(0, 10);
        const byAction = last.reduce((acc, d) => {
          const a = d.action || "EVALUATE";
          acc[a] = (acc[a] || 0) + 1;
          return acc;
        }, {});
        const avgZi = Math.round(last.reduce((s, d) => s + (d.final_score ?? d.ziScore ?? 0), 0) / last.length);
        const actionColor = { BUY: "#4ade80", ENTER: "#4ade80", HOLD: "#60a5fa", EXIT: "#f87171", SELL: "#f87171", EVALUATE: "#fbbf24" };
        return (
          <div data-testid="journal-insights" style={{
            marginBottom: 14, padding: 12, borderRadius: 12,
            background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(96,165,250,0.04))",
            border: "1px solid rgba(167,139,250,0.18)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#a78bfa" }}>SNAPSHOT · LAST {last.length}</span>
              <span style={{ fontSize: 10, color: "#8b949e" }}>Avg Zi <span style={{ fontWeight: 800, color: avgZi >= 75 ? "#4ade80" : avgZi >= 55 ? "#fbbf24" : "#f87171" }}>{avgZi}</span></span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {Object.entries(byAction).map(([act, n]) => (
                <span key={act} style={{
                  padding: "4px 10px", borderRadius: 8,
                  background: `${actionColor[act] || "#8b949e"}18`,
                  color: actionColor[act] || "#8b949e",
                  fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                }}>{act} {n}</span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Regime transition ribbon — last 30 decisions (oldest left → newest right) */}
      {ribbon.length > 1 && (
        <div data-testid="regime-ribbon" style={{ marginBottom: 14, padding: 10, borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ fontSize: 9, color: "#8b949e", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
            <span>Regime · last {ribbon.length}</span>
            <span style={{ color: "#636e7b" }}>oldest ← → latest</span>
          </div>
          <div style={{ display: "flex", gap: 1, height: 14, borderRadius: 4, overflow: "hidden" }}>
            {ribbon.map((r, i) => (
              <div
                key={i}
                title={`${(r.r || "unknown").replace(/_/g, " ")} — ${r.t ? new Date(r.t).toLocaleString() : ""}`}
                style={{ flex: 1, background: regimeColors[r.r] || "#64748b", opacity: 0.9 }}
              />
            ))}
          </div>
          {/* Regime legend — only regimes actually present in the ribbon */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
            {Array.from(new Set(ribbon.map(r => r.r))).map(rn => (
              <div key={rn} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: "#8b949e" }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: regimeColors[rn] || "#64748b" }} />
                {(rn || "unknown").replace(/_/g, " ")}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regime filter chips */}
      {allRegimes.length > 0 && (
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6, marginBottom: 14 }} data-testid="journal-regime-filters">
          {["ALL", ...allRegimes].map(r => {
            const active = regimeFilter === r;
            return (
              <button
                key={r}
                data-testid={`regime-chip-${r.toLowerCase()}`}
                onClick={() => { hapticTap(); setRegimeFilter(r); }}
                style={{
                  padding: "6px 12px", borderRadius: 14, border: "1px solid",
                  borderColor: active ? "#a78bfa" : "rgba(255,255,255,0.08)",
                  background: active ? "rgba(167,139,250,0.2)" : "rgba(255,255,255,0.03)",
                  color: active ? "#a78bfa" : "#e6edf3",
                  fontSize: 11, fontWeight: 700, cursor: "pointer",
                  whiteSpace: "nowrap", WebkitTapHighlightColor: "transparent",
                }}
              >{r.replace(/_/g, " ")}</button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 && <div style={{ padding: 20, fontSize: 12, color: "#8b949e", textAlign: "center" }}>{items.length === 0 ? "No decisions logged yet. Autopilot will populate this feed as it scans." : `No decisions in ${regimeFilter.replace(/_/g, " ")} regime.`}</div>}
      {filtered.map((d, i) => {
        const act = d.action || "EVALUATE";
        const actColor = act === "BUY" || act === "ENTER" ? "#4ade80" : act === "EXIT" || act === "SELL" ? "#f87171" : act === "HOLD" ? "#60a5fa" : "#fbbf24";
        const final = d.final_score ?? d.ziScore ?? 0;
        const vectors = d.vectors || d.original_vectors || {};
        const itemKey = d.decision_id || d.id || `${d.symbol || "?"}-${i}`;
        const isReplaying = replayId === itemKey;
        const p = isReplaying ? replayProgress : 1;
        return (
          <div key={itemKey} data-testid={`journal-item-${i}`} style={{ marginBottom: 12, padding: 14, borderRadius: 14, background: isReplaying ? "rgba(167,139,250,0.05)" : "rgba(255,255,255,0.02)", border: `1px solid ${isReplaying ? "rgba(167,139,250,0.25)" : "rgba(255,255,255,0.05)"}`, transition: "background 0.3s ease, border-color 0.3s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: "#e6edf3" }}>{d.symbol || d.ticker || "?"}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 5, background: `${actColor}22`, color: actColor, letterSpacing: 1 }}>{act}</span>
                {d.regime && <span style={{ fontSize: 10, color: "#a78bfa", padding: "2px 6px", borderRadius: 4, background: "rgba(167,139,250,0.1)" }}>{String(d.regime).replace("_", " ")}</span>}
              </div>
              <div style={{ textAlign: "right", display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  data-testid={`replay-${i}`}
                  onClick={(e) => { e.stopPropagation(); playReplay(itemKey); }}
                  aria-label="Replay decision"
                  style={{
                    width: 28, height: 28, borderRadius: 14, border: "1px solid rgba(167,139,250,0.28)",
                    background: isReplaying ? "rgba(167,139,250,0.2)" : "rgba(167,139,250,0.08)",
                    color: "#a78bfa", cursor: "pointer",
                    fontSize: 11, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >{isReplaying && replayProgress < 1 ? "…" : "▶"}</button>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: actColor, lineHeight: 1 }}>Zi {Math.round(final * p)}</div>
                  <div style={{ fontSize: 9, color: "#636e7b" }}>{d.createdAt ? new Date(d.createdAt).toLocaleString() : ""}</div>
                </div>
              </div>
            </div>
            {d.reason && <div style={{ fontSize: 11, color: "#c9d1d9", lineHeight: 1.5, marginBottom: 8 }}>{d.reason}</div>}
            {Object.keys(vectors).length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
                {Object.entries(vectors).slice(0, 12).map(([k, v], vi) => {
                  const val = typeof v === "object" ? v.raw || v.value || 0 : v;
                  const fullPct = Math.max(0, Math.min(100, val));
                  // Staggered ramp so each vector "climbs" at a slightly different pace during replay.
                  const delay = (vi % 12) * 0.04;
                  const localP = Math.max(0, Math.min(1, (p - delay) / (1 - delay || 1)));
                  const pct = isReplaying ? fullPct * localP : fullPct;
                  const vc = fullPct >= 70 ? "#4ade80" : fullPct >= 40 ? "#fbbf24" : "#f87171";
                  return (
                    <div key={k} style={{ padding: "4px 6px", borderRadius: 6, background: "rgba(0,0,0,0.25)" }}>
                      <div style={{ fontSize: 8, color: "#636e7b", letterSpacing: 0.5, textTransform: "uppercase", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{k.replace(/_/g, " ")}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
                        <div style={{ flex: 1, height: 3, borderRadius: 1.5, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: vc, transition: isReplaying ? "none" : "width 0.3s ease" }} />
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 700, color: vc, minWidth: 18, textAlign: "right" }}>{Math.round(pct)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
